/**
 * Convert a Recharts SVG element into a PNG byte stream for pdf-lib embedding.
 *
 * We render the chart at 2× resolution to make it look crisp in the PDF, then
 * downsample naturally during PDF render. This is the only place we touch a
 * canvas; the rest of the report pipeline is canvas-free.
 */

export async function svgElementToPng(
  svg: SVGSVGElement,
  scale = 2,
): Promise<Uint8Array> {
  const xml = new XMLSerializer().serializeToString(svg);
  // Make sure xmlns is present (Recharts usually includes it; double-check)
  const xmlWithNs = xml.includes("xmlns=")
    ? xml
    : xml.replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ');

  const svgBlob = new Blob([xmlWithNs], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("svg_image_load_failed"));
      i.src = url;
    });

    const w = Math.round((svg.viewBox?.baseVal.width || svg.clientWidth || 800) * scale);
    const h = Math.round((svg.viewBox?.baseVal.height || svg.clientHeight || 360) * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas_2d_not_available");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas_toBlob_failed"))),
        "image/png",
      );
    });
    const buffer = await blob.arrayBuffer();
    return new Uint8Array(buffer);
  } finally {
    URL.revokeObjectURL(url);
  }
}
